import BigNumber from 'bignumber.js';
import { formatUnits, parseUnits } from 'viem';

const commaFormatted = (amount: string, bufferStr?: string) => {
  const delimiter = ',';
  let a = amount.split('.', 2);
  const d = a[1] || undefined;
  // eslint-disable-next-line radix
  let i = parseInt(a[0]);

  // eslint-disable-next-line no-restricted-globals
  if (isNaN(i)) {
    return '';
  }
  let minus = '';
  if (i < 0 || (bufferStr && bufferStr.startsWith('-'))) {
    minus = '-';
  }
  i = Math.abs(i);
  // eslint-disable-next-line no-new-wrappers
  let n = new String(i);
  a = [];
  while (n.length > 3) {
    const nn = n.substr(n.length - 3);
    a.unshift(nn);
    n = n.substr(0, n.length - 3);
  }
  if (n.length > 0) {
    a.unshift(n as string);
  }
  n = a.join(delimiter);
  if (!d || d.length < 1) {
    // eslint-disable-next-line no-param-reassign
    amount = n as string;
  } else {
    // eslint-disable-next-line no-param-reassign
    amount = `${n}.${d}`;
  }
  // eslint-disable-next-line no-param-reassign
  amount = minus + amount;
  return amount;
};

const formatAmount = (
  value: string | number | BigNumber | undefined | null,
  decimal?: number,
): string => {
  if (!value) {
    return '0';
  }

  let bufferNum = value;
  if (decimal) {
    const numberTypeConvert = typeof value === 'string' ? parseFloat(value) : value;
    const decimalFilter = 10 ** decimal;
    const roundedNumber =
      parseInt(`${(numberTypeConvert as number) * decimalFilter}`, 10) / decimalFilter;

    bufferNum = roundedNumber;
  }

  const numSplit = (bufferNum || 0).toString().split('.');
  // NOTE numSplit[0] = numSplit[0]?.replace(SPLIT_NUMBER_REGEX, ',');
  numSplit[0] = commaFormatted(numSplit[0]);

  return numSplit.join('.');
};

const formatMoney = (amount: string | number, digit: number = 2) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digit,
  });

  return formatter.format(Math.trunc(Number(amount)));
};

const formatMoneyWithoutTrunc = (amount: string | number, digit: number = 2) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digit,
  });

  return formatter.format(Number(amount));
};

const formatCryptoBalance = (balance: bigint | undefined, decimals: number | undefined) => {
  if (!balance) return 0;
  return formatUnits(balance, decimals || 18);
};

const convertCryptoBalance = (balance: string | undefined, decimals: number | undefined) => {
  if (!balance) return 0n;
  return parseUnits(balance, decimals || 18);
};

const roundingNumber = (balance: string) => {
  const number = Number(balance);
  const formattedNumber = (Math.round(number * 100) / 100).toFixed(2);
  return parseFloat(formattedNumber);
};

const formatAmountV2 = (
  value: string | number | BigNumber | undefined | null,
  decimal: number = 8,
): string => {
  if (value === undefined || value === null || value === '') {
    return '0';
  }

  let bufferNum: BigNumber | number | string;

  // Convert string to number with 8 decimals or keep as BigNumber
  if (typeof value === 'string') {
    bufferNum = new BigNumber(value).toFixed(decimal);
  } else if (typeof value === 'number') {
    bufferNum = new BigNumber(value).toFixed(decimal);
  } else if (value instanceof BigNumber) {
    bufferNum = value.toFixed(decimal);
  } else {
    return '0';
  }

  // Convert to string and remove trailing zeros from decimal places
  let bufferStr = bufferNum.toString();

  // Split into integer and decimal parts
  const numSplit = bufferStr.split('.');

  if (numSplit.length > 1) {
    // If decimal part is all zeros, remove it
    if (parseFloat(numSplit[1]) === 0) {
      bufferStr = numSplit[0];
    } else {
      // Otherwise, trim trailing zeros
      numSplit[1] = numSplit[1].replace(/0+$/, '');
      bufferStr = numSplit.join('.');
    }
  }

  // Format the integer part with commas if needed
  numSplit[0] = commaFormatted(numSplit[0], bufferStr);
  return parseFloat(numSplit[1]) > 0 ? numSplit.join('.') : numSplit[0];
};

const formatCompactNumber = (number: number) => {
  const units = ['', 'K', 'M', 'B', 'T'];
  let unitIndex = 0;

  while (number >= 1000 && unitIndex < units.length - 1) {
    number /= 1000;
    unitIndex++;
  }

  return parseFloat(number.toFixed(1)) + units[unitIndex];
};

export {
  formatAmount,
  formatMoney,
  formatCryptoBalance,
  convertCryptoBalance,
  roundingNumber,
  formatMoneyWithoutTrunc,
  formatAmountV2,
  formatCompactNumber,
};
